// src/components/ForgotPassword.js
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import cloudImage from "../img/athrv-cloudimage-5.png";
import athrvLogo from "../img/athrv-cloud-logo-1.png"; 

const ForgotPassword = () => {
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const api = process.env.REACT_APP_API_NEW_HOST_URL;

  const LogoHeader = () => {
    return (
      <Box
        component="img"
        src={athrvLogo}
        alt="Athrv Cloud"
        sx={{
          display: "block",
          mx: "auto", // shorthand for marginLeft and marginRight auto
          mb: 3,
          maxWidth: 150, // adjust to your desired max width
          height: "auto",
        }}
      />
    );
  };

  const handleSendOtp = async () => {
    if (!email) {
      setError("Please enter your email");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${api}/athrv/api/v1/auth/forgot-password-request?email=${email}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to send OTP");

      setStep(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setError("Please enter the OTP");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${api}/athrv/api/v1/auth/forgot-password-verify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp }),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Invalid OTP");

      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${api}/athrv/api/v1/auth/forgot-password-reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, new_password: newPassword }),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Password reset failed");

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const steps = ["Enter Email", "Verify OTP", "Reset Password"];

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: "50%",
            backgroundImage: `url(${cloudImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      <Box
        sx={{
          ml: !isMobile ? "50%" : 0,
          // height: "100vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          scrollbarGutter: "stable",
          // pt: isMobile ? 4 : 2, // extra padding top for mobile
        }}
      >
        <Paper
          elevation={6}
          sx={{ p: 4, width: "100%", maxWidth: 450, borderRadius: 3 }}
        >
          {!success ? (
            <>
              <LogoHeader />
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "#0C1A32",
                }}
              >
                Forgot Password
              </Typography>

              <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {step === 0 && (
                <>
                  <TextField
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {error && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {error}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSendOtp}
                    disabled={loading}
                    sx={{
                      backgroundColor: "#0C1A32",
                      "&:hover": { backgroundColor: "#002244" },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Send OTP"
                    )}
                  </Button>

                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      cursor: "pointer",
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                      mb: 2,
                      mt: 3,
                    }}
                    onClick={() => navigate("/login")}
                  >
                    Go To Login Page
                  </Typography>
                </>
              )}

              {step === 1 && (
                <>
                  <TextField
                    label="Enter OTP"
                    variant="outlined"
                    fullWidth
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {error && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {error}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleVerifyOtp}
                    disabled={loading}
                    sx={{
                      backgroundColor: "#0C1A32",
                      "&:hover": { backgroundColor: "#002244" },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Verify OTP"
                    )}
                  </Button>

                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      cursor: "pointer",
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                      mb: 2,
                      mt: 3,
                    }}
                    onClick={() => navigate("/login")}
                  >
                    Go To Login Page
                  </Typography>
                </>
              )}

              {step === 2 && (
                <>
                  <TextField
                    label="New Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {error && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {error}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleResetPassword}
                    disabled={loading}
                    sx={{
                      backgroundColor: "#0C1A32",
                      "&:hover": { backgroundColor: "#002244" },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>

                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      cursor: "pointer",
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                      mb: 2,
                      mt: 3,
                    }}
                    onClick={() => navigate("/login")}
                  >
                    Go To Login Page
                  </Typography>
                </>
              )}
            </>
          ) : (
            <Box textAlign="center">
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Password reset successfully!
              </Typography>
              <Button
                variant="contained"
                onClick={handleBackToLogin}
                sx={{
                  backgroundColor: "#0C1A32",
                  "&:hover": { backgroundColor: "#002244" },
                  mt: 2,
                }}
              >
                Go Back to Login Page
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ForgotPassword;